// Canonical Prior Auth status maps — single source of truth for the PA table
// chips and the Settings → Action Needed labels. Statuses persist as display
// strings ("Requires review"); Settings stores enum keys ("REQUIRES_REVIEW").

export const WAITING_ON_PAYER_DISPLAY = "Waiting on payer";
export const EXPIRY_WARNING_DAYS = 14;
export const NEEDS_ATTENTION_DISPLAY = "Needs Attention";

export const PA_STATUS_VALUE_BY_KEY = {
  INITIATED: "Initiated",
  CREATED: "Created",
  INCOMPLETE: "Incomplete",
  NOT_SUPPORTED: "Payor/Medication not supported",
  REQUIRES_REVIEW: "Requires review",
  DEMOGRAPHICS_REVIEW: "Review demographics info",
  PROCESSING: "Processing",
  // Key must equal backend WAITING_ON_PAYER_STATUS_KEY.
  WAITING_ON_PAYER: WAITING_ON_PAYER_DISPLAY,
  // Key must equal backend NEEDS_ATTENTION_STATUS_KEY (stuck-pipeline pseudo-status).
  NEEDS_ATTENTION: NEEDS_ATTENTION_DISPLAY,
  CLINICAL_REVIEW: "Review clinical info",
  SENT_TO_PLAN: "Sent to plan",
  SENT_TO_PRESCRIBER: "Sent to prescriber",
  APPROVED: "Approved",
  RESPONSE_RECEIVED: "Response received",
  DENIED: "Denied",
  ARCHIVED: "Archived",
  NO_PAYOR_RESPONSE_RECEIVED: "No payor response received",
  REQUIRES_MORE_INFORMATION: "Requires more information",
  PA_NOT_REQUIRED: "Prior authorization not required",
};

// CMM statuses where the submission can be re-opened (edit + resubmit).
export const CMM_REOPENABLE_STATUSES = [
  "Payor/Medication not supported",
  "Response received",
  "Requires more information",
];

export const PA_STATUS_MAP = {
  Processing: { label: "Processing", color: "error" },
  [WAITING_ON_PAYER_DISPLAY]: { label: WAITING_ON_PAYER_DISPLAY, color: "warning" },
  Created: { label: "Fetching Questions", color: "error" },
  "Sent to prescriber": { label: "Sent to Prescriber", color: "warning" },
  "Sent to plan": { label: "Sent to Plan", color: "success" },
  "Response received": { label: "Review Response", color: "warning" },
  Denied: { label: "Denied", color: "error" },
  Incomplete: { label: "Incomplete", color: "warning" },
  Approved: { label: "Approved", color: "success" },
  Initiated: { label: "Initiated", color: "warning" },
  "Requires more information": { label: "Requires More Info", color: "warning" },
  "No payor response received": { label: "No Payor Response", color: "warning" },
  "Payor/Medication not supported": { label: "Not Supported", color: "error" },
  "Requires review": { label: "Requires Review", color: "info" },
  "Review demographics info": { label: "Demographics Review", color: "info" },
  "Review clinical info": { label: "Clinical Review", color: "info" },
  Archived: { label: "Archived", color: "secondary" },
  "Prior authorization not required": { label: "PA Not Required", color: "info" },
  [NEEDS_ATTENTION_DISPLAY]: { label: NEEDS_ATTENTION_DISPLAY, color: "error" },
};

// Enum key → display label, derived so Settings toggles read the same labels as the table chips.
export const PA_STATUS_LABEL_BY_KEY = Object.fromEntries(
  Object.entries(PA_STATUS_VALUE_BY_KEY).map(([key, value]) => [key, PA_STATUS_MAP[value]?.label ?? value])
);

export const PAYER_WAIT_PIPELINE_STEPS = new Set(["Fetch Payor Response", "Repeat Polling for Definitive Response"]);

// Backend mirror: OrderSubmission.SubmissionPipelineSteps.RETRIES_EXHAUSTED — the terminal
// failure step of every submission pipeline (CMM, fax, forward-to-provider).
export const RETRIES_EXHAUSTED_PIPELINE_STEP = "Retries Failed - Handle Manually";
export const CMM_SUBMISSION_TYPE = "CoverMyMeds";

// Needs Attention marks the one stuck-pipeline shape the USER must resolve: a CMM import
// whose demographics fetch failed, awaiting a corrected re-import (the reimport banner's
// predicate). Other RETRIES_EXHAUSTED failures are handled by backend ops and keep their
// real status. Backend mirror: needs_attention_q in portal/logic/order_status_query.py.
export function resolvePaDisplayStatus(status, pipelineStep, submissionType) {
  if (
    submissionType === CMM_SUBMISSION_TYPE &&
    pipelineStep === RETRIES_EXHAUSTED_PIPELINE_STEP &&
    status === PA_STATUS_VALUE_BY_KEY.CREATED
  ) {
    return NEEDS_ATTENTION_DISPLAY;
  }
  if (status === PA_STATUS_VALUE_BY_KEY.PROCESSING && PAYER_WAIT_PIPELINE_STEPS.has(pipelineStep)) {
    return WAITING_ON_PAYER_DISPLAY;
  }
  return status;
}

/** The PA list's status filter, in the order specialists scan it; values are status keys or pseudo-statuses. */
export const PA_STATUS_FILTER_OPTIONS = [
  { title: "All Submissions", value: "" },
  { title: "Incomplete", value: "INCOMPLETE" },
  { title: "Requires Review", value: "REQUIRES_REVIEW" },
  { title: "Demographics Review", value: "DEMOGRAPHICS_REVIEW" },
  { title: "Clinical Review", value: "CLINICAL_REVIEW" },
  { title: "Initiated", value: "INITIATED" },
  { title: "Fetching Questions", value: "CREATED" },
  { title: "Processing", value: "PROCESSING" },
  { title: "Waiting on Payer", value: "WAITING_ON_PAYER" },
  { title: "Needs Attention", value: "NEEDS_ATTENTION" },
  { title: "Sent to Plan", value: "SENT_TO_PLAN" },
  { title: "Sent to Prescriber", value: "SENT_TO_PRESCRIBER" },
  { title: "Approved", value: "APPROVED" },
  { title: "Denied", value: "DENIED" },
  { title: "PA Not Required", value: "PA_NOT_REQUIRED" },
  { title: "Archived", value: "ARCHIVED" },
  { title: "Not Supported", value: "NOT_SUPPORTED" },
  { title: "No Payor Response", value: "NO_PAYOR_RESPONSE_RECEIVED" },
  { title: "Requires More Info", value: "REQUIRES_MORE_INFORMATION" },
  { title: "Review Response", value: "RESPONSE_RECEIVED" },
];
